<script setup lang="ts">
import { toRefs, ref, onMounted, watch, reactive, onUpdated } from 'vue';

// pinia
import StoreAuth from '@store/auth';
import Store from '@store/dataProvider';

// composables
import { useMessage } from '../../../composables/useMessage';
import { useToast } from '../../../composables/useToast';

// types
import { IUser, IRole, IPermission } from '../../../types/user.type';
import { IAgents } from './../../../types/agents.type';
import { IClient } from './../../../types/client.type';
import { ITemplateUI } from '../../../types/templatesUI.type';
import { ILanguage } from '../../../types/languages.type';
import { ICompany } from '../../../types/company.type';
import { IReinsurer } from '../../../types/reinsurer.type';
import { ITypeUser } from '../../../types/typeUsers.type';
import { ICountry } from '../../../types/countries.type';

// repositories
import UserApi from '@repositories/user.repository';

// validations
import { insertHtmlValidations, validateFormat } from './handleValidator';
import CountriesRepository from '../../../repositories/countries.repository';
import { useI18n } from 'vue-i18n';
import { applyTypeUserVisibility } from '../../../helpers/typeUserVisibility';

const { t } = useI18n();
const store = Store();
const storeAuthProvider = StoreAuth();

const userApiRepository = new UserApi();

const { $toast } = useToast();
const { messageHTML, showMessage } = useMessage();

const props = defineProps({
  userData: {
    type: Object as () => IUser,
    default: () => ({
      user: {
        apiKey: {},
        apiKeyId: '',
        createdAt: '',
        email: '',
        id: '',
        typeUserId: null,
        permissionDescriptions: [],
        rolesDescriptions: [],
        countries: [],
        urlDomain: '',
      },
    }),
  },
  companies: {
    type: Object as () => Array<ICompany>,
  },
  reinsurers: {
    type: Object as () => Array<IReinsurer>,
  },
  agents: {
    type: Object as () => Array<IAgents>,
  },
  clients: {
    type: Object as () => Array<IClient>,
  },
  typeUsers: {
    type: Object as () => Array<ITypeUser>,
  },
  templatesUI: {
    type: Object as () => Array<ITemplateUI>,
    default: () => ({
      templatesUI: {
        id: '',
        name: '',
      },
    }),
  },
  languages: {
    type: Object as () => Array<ILanguage>,
  },
});

const emit = defineEmits<{
  (e: 'closeModal'): void;
}>();

const { typeUsers, userData, agents, templatesUI, languages, companies, clients, reinsurers } =
  toRefs(props);
// let user = { ...userData.value };

const RolesData = ref<IRole[]>([]);
const isLoading = ref(true);
const filterRolesRef = ref<IRole[]>([]);
const countriesData = ref<ICountry[]>([]);
const user = ref<IUser>({...userData.value});
const permissionsActive = ref<IPermission[]>([]);
const selectedRoles = ref<IRole[]>([]);
const toogleViewRoles = ref<boolean>(true);
const passwordValidate: any = reactive({
  containNumber: /.*\d+.*/,
  containUppercase: /[A-Z]/,
  containLowercase: /[a-z]/,
  containSymbol: /.*[\W_].*/,
  minLength: /^.{8,}$/,
});

onMounted(async () => {
  const data = await userApiRepository.getRoles();
  const dataCountries = await CountriesRepository.getcountries();
  countriesData.value = dataCountries.data.countries.map((e: ICountry) => ({
    id: e.id,
    nombre: e.description,
  }));
  RolesData.value = data;
  isLoading.value = false;
  applyTypeUserVisibility(
      user.value.typeUserId,
      fieldsUpdateUser.value,
    );
    return;
});

onUpdated(async () => {
  insertHtmlValidations(passwordValidate);
});

watch(RolesData, (newRolesData) => {
  if (!isLoading.value && userData.value && userData.value.rolesDescriptions) {
    const namesToFilter = userData.value.rolesDescriptions;
    filterRolesRef.value = newRolesData.filter((role: any) =>
      namesToFilter.includes(role.title),
    );
  }
});

const fieldsUpdateUser: any = ref([
  {
    key: 'email',
    title: t('ajustes.usuarios.modalUpdateUser.email_del_usuario'),
    type: 'email',
    validations: {
      rules: {
        required: true,
        email: true,
      },
      messages: {
        required: t('global.es_obligatorio'),
        email: t('global.email_invalido'),
      },
    },
    placeholder: t('ajustes.usuarios.modalUpdateUser.email_del_usuario'),
    value: user.value.email,
    cols: 6,
  },
  {
    key: 'fullName',
    title: t('ajustes.usuarios.modalUpdateUser.nombre_completo'),
    type: 'text',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('ajustes.usuarios.modalUpdateUser.nombre_completo'),
    value: user.value.fullName,
    cols: 6,
  },
  {
    key: 'urlDomain',
    title: t('ajustes.usuarios.modalCreateUser.url_dominio'),
    type: 'text',
    placeholder: t('ajustes.usuarios.modalCreateUser.url_dominio'),
    value: user.value.urlDomain,
    cols: 6,
  },
  {
    key: 'typeUserId',
    title: t('ajustes.usuarios.modalCreateUser.tipo_usuario'),
    type: 'select',
    placeholder: t('ajustes.usuarios.modalCreateUser.tipo_usuario'),
    value: user.value.typeUserId,
    // label: 'type',
    customLabel: (e: any) => t(`typeUsers.${e.type}`),
    cols: 6,
    multiple: false,
    options: typeUsers?.value,
    formatValue: (e: ITypeUser) => e?.id,
    listenSelect: true,
  },
  {
    key: 'companyId',
    title: t('global.compania'),
    type: 'select',
    placeholder: t('global.compania'),
    value: user.value.companyId,
    selectLabel: null,
    multiple: false,
    cols: 6,
    options: companies?.value,
    formatValue: (e: ICompany) => e?.id,
    hidden: false,
  },
  //reasegurador aca lo dejo!!! debo llamar los reaseguradores al backend
  // si un agente puede tener muchas companias entonces un cliente debe tener una sola compania
  {
    key: 'reinsurerId',
    title: t('global.reasegurador'),
    label: 'name',
    type: 'select',
    placeholder: t('global.reasegurador'),
    value: user.value.reinsurerId,
    selectLabel: null,
    multiple: false,
    cols: 6,
    options: reinsurers?.value,
    formatValue: (e: IReinsurer) => e?.id,
    hidden: false,
  },
  {
    key: 'agentId',
    customLabel: (e: any) => `${e.name} - [${e?.rfc || e?.rut || e?.nif || e?.cuit}]`,
    searchable: true,
    title: t(
      'ajustes.usuarios.modalUpdateUser.agente',
    ),
    type: 'select',
    placeholder: t(
      'ajustes.usuarios.modalUpdateUser.selecciona_uno_varios_ninguno',
    ),
    value: user.value.agentId,
    cols: 6,
    options: agents?.value,
    formatValue: (e: IAgents) => e?.id ?? null,
    multiple: false,
  },
  {
    key: 'clientId',
    label: 'tradeName',
    searchable: true,
    title: t(
      'ajustes.usuarios.modalUpdateUser.cliente',
    ),
    type: 'select',
    placeholder: t(
      'ajustes.usuarios.modalUpdateUser.selecciona_uno_varios_ninguno',
    ),
    value: user.value.clientId,
    cols: 6,
    options: clients?.value,
    formatValue: (e: IClient) => e?.id,
    multiple: false,
  },
  {
    key: 'countries',
    title: t(
      'ajustes.usuarios.modalUpdateUser.selecciona_los_paises_del_usuario',
    ),
    selectLabel: null,
    type: 'select',
    placeholder: t('ajustes.usuarios.modalUpdateUser.seleccionar_paises'),
    value: user.value.countries.map((e: ICountry) => ({
      id: e.id,
      nombre: e.description,
    })),
    cols: 6,
    options: countriesData,
    validations: {
      rules: {
        required: true,
      },
    },
    hidden: false,
    multiple: true,
    listenSelect: false,
    listenRemove: false,
  },
  {
    key: 'roles',
    title: t(
      'ajustes.usuarios.modalUpdateUser.selecciona_los_roles_del_usuario',
    ),
    selectLabel: null,
    label: 'title',
    type: 'select',
    placeholder: t('ajustes.usuarios.modalUpdateUser.seleccionar_roles'),
    value: filterRolesRef,
    cols: 6,
    options: RolesData,
    validations: {
      rules: {
        required: true,
      },
    },
    hidden: false,
    multiple: true,
    listenSelect: true,
    listenRemove: true,
  },
  {
    key: 'hasTwoFactorAuthentication',
    title: t('ajustes.usuarios.modalUpdateUser.segundo_factor'),
    type: 'select',
    placeholder: 'Elije una opcion',
    value: [
      {
        nombre: !user.value.hasTwoFactorAuthentication ? 'NO' : 'SI',
        id: user.value.hasTwoFactorAuthentication,
      },
    ],
    selectLabel: null,
    cols: 6,
    options: [
      { nombre: 'NO', id: 0 },
      { nombre: 'SI', id: 1 },
    ],
  },
  {
    key: 'templateId',
    title: t('ajustes.usuarios.modalUpdateUser.plantilla'),
    type: 'select',
    placeholder: t('global.elije_una_opcion'),
    value: user.value.templateId
      ? [
          {
            nombre: templatesUI.value.find((e: any) => e.id == user.value.templateId)
              ?.name,
            id: user.value.templateId,
          },
        ]
      : [{ nombre: t('global.ninguna'), id: 0 }],
    selectLabel: null,
    cols: 6,
    options: templatesUI?.value
      ?.map((e) => ({ nombre: e.name, id: e.id }))
      .concat([{ nombre: t('global.ninguna'), id: 0 }]),
  },
  {
    key: 'languageId',
    title: t('ajustes.usuarios.modalUpdateUser.idioma'),
    type: 'select',
    placeholder: t('global.elije_una_opcion'),
    value: user.value.languageId,
    selectLabel: null,
    cols: 6,
    options: languages?.value?.map((e: ILanguage) => ({
      nombre: e.language,
      id: e.id,
    })),
  },
]);

const fieldsUpdatePassword: any = ref([
  {
    key: 'password',
    title: t('ajustes.usuarios.modalUpdateUser.contrasenia'),
    type: 'password',
    validations: {
      rules: {
        required: true,
        min: 8,
        verifyExpreRegular:
          /^(?=.*[!@#$%^&*()_+{}\[\]:;<>,.?~\\-])(?=.*[A-Z])(?=.*[a-z])(?=.*\d).+$/,
      },
    },
    placeholder: 'Ejemplo.123',
    value: null,
    cols: 6,
    customClass: 'inputs-password',
    formatValue: (e: any) => validateFormat(e, passwordValidate),
  },
  {
    key: 'repeatPassword',
    title: t('ajustes.usuarios.modalUpdateUser.repetir_contrasenia'),
    type: 'password',
    validations: {
      rules: {
        required: true,
      },
    },
    placeholder: t('ajustes.usuarios.modalUpdateUser.repetir_contrasenia'),
    value: null,
    cols: 6,
  },
]);

const handleSelect = async (e: any) => {
  if (e.input.key === 'typeUserId' && e.value?.id) {
    await applyTypeUserVisibility(
      e.value.id,
      fieldsUpdateUser.value,
    );
    return;
  }

  if (selectedRoles.value.length === 0 && e.input.key === 'roles') {
    selectedRoles.value = e.value;
    const { data } = await userApiRepository.getPermissionsByRoleId(
      e.value[0].id,
    );
    permissionsActive.value = data;
  } else {
    const differentObject = e.value.find(
      (itemA: any) =>
        !selectedRoles.value.some((itemB) => itemA.id === itemB.id),
    );

    if (differentObject) {
      selectedRoles.value = e.value;

      const { data } = await userApiRepository.getPermissionsByRoleId(
        differentObject.id,
      );

      permissionsActive.value = [...permissionsActive.value, ...data];
      permissionsActive.value = permissionsActive.value.filter(
        (item, index, self) =>
          index === self.findIndex((obj) => obj.id === item.id),
      );
    }
  }
};

const handleRemove = async (e: any) => {
  selectedRoles.value = selectedRoles.value.filter((i) => i.id !== e.value.id);

  if (!selectedRoles.value.length) {
    permissionsActive.value = [];
  }

  for (const role of selectedRoles.value) {
    const { data } = await userApiRepository.getPermissionsByRoleId(role.id);

    permissionsActive.value = data;
  }
};

const handleUpdateUser = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;

  const updateUserData = {
    ...items,
    templateId: items.templateId.id,
    languageId: items.languageId?.id ?? null,
    hasTwoFactorAuthentication: items.hasTwoFactorAuthentication.id,
  };

  try {
    const { data } = await userApiRepository.updateUser(
      user.value.id,
      updateUserData,
    );
    if (!data) throw new Error();
    if (
      storeAuthProvider.user &&
      data.templateId &&
      data.id === storeAuthProvider.user.id
    ) {
      const template = templatesUI?.value?.find(
        (e) => e.id === data.templateId,
      );
      if (template) {
        storeAuthProvider.setProp({
          key: 'user',
          index: 'template',
          value: template,
        });
      }
    } else {
      storeAuthProvider.setProp({
        key: 'user',
        index: 'template',
        value: null,
      });
    }
    let index = store.users.findIndex((value: any) => value.id === data.id);
    if (index > -1) {
      store.setProp({ key: 'users', index, value: data });
    }
    emit('closeModal');
  } catch (error: any) {
    showMessage(error.message, 'error');
  }
};

const handleToggleButtonClick = () => {
  toogleViewRoles.value = !toogleViewRoles.value;
};

const handleUpdatePassword = async ({ items, isFormValid }: any) => {
  if (!isFormValid) return;
  if (items.password !== items.repeatPassword) {
    return showMessage(
      t('ajustes.usuarios.modalUpdateUser.las_contrasenias_deben_ser_iguales'),
      'error',
    );
  } else {
    showMessage(null);
  }

  try {
    await userApiRepository.updateUser(user.value.id, { password: items.password });

    $toast?.success(
      t(
        'ajustes.usuarios.modalUpdateUser.la_contraseña_ha_si_cambiada_correctamente',
      ),
    );

    emit('closeModal');
  } catch (error: any) {
    showMessage(error.message, 'error');
  }
};
</script>

<template>
  <CButton
    class="background-pink button__form mt-3 w-100"
    style="padding: 0.5rem !important"
    type="button"
    @click="handleToggleButtonClick"
  >
    <template #content>{{
      toogleViewRoles
        ? t('ajustes.usuarios.modalUpdateUser.ir_a_cambiar_constrasenia')
        : t('ajustes.usuarios.modalUpdateUser.ir_a_actualizar_usuario')
    }}</template>
  </CButton>
  <div v-if="toogleViewRoles">
    <Form
      ref="refTableForm"
      :inputs="fieldsUpdateUser"
      @onSubmit="handleUpdateUser"
      @select="handleSelect"
      @remove="handleRemove"
    >
      <template #buttons>
        <p v-if="permissionsActive.length">
          {{ t('ajustes.usuarios.modalUpdateUser.permisos_activos') }}
        </p>
        <ul v v-for="permission in permissionsActive" :key="permission.id">
          <li>{{ permission.descripcion }}</li>
        </ul>
        <div v-if="messageHTML" v-html="messageHTML" />
        <div class="d-flex">
          <CButton class="w-100 fs-6 p-2" background="background-blue">
            <template #content>
              <div>
                <span> {{ t('global.actualizar') }}</span>
              </div>
            </template>
          </CButton>
        </div>
      </template>
    </Form>
  </div>
  <div v-if="!toogleViewRoles">
    <p class="m-0">{{ t('global.usuario') }}: {{ user.email }}</p>
    <Form
      ref="refTableForm"
      :inputs="fieldsUpdatePassword"
      @onSubmit="handleUpdatePassword"
    >
      <template #buttons>
        <div v-if="messageHTML" v-html="messageHTML" />
        <div class="d-flex">
          <CButton class="w-100 fs-6 p-2" background="background-blue">
            <template #content>
              <div>
                <span>{{
                  t('ajustes.usuarios.modalUpdateUser.actualizar_contrasenia')
                }}</span>
              </div>
            </template>
          </CButton>
        </div>
      </template>
    </Form>
  </div>
</template>
